'use client';

import { useEffect, useRef, useState } from 'react';
import { Backward10SecsSvg, CloseSvg, Forward15SecsSvg, PauseSvg, PlaySvg } from '@/assets/Icons';
import MarqueeBox from './MarqueeBox';
import formatTime from '@/helpers/formatTime';
import DorsaImage from '@/components/dorsaImage/DorsaImage';
import { IMAGE_SHAPES, IMAGE_SIZES } from '@/components/dorsaImage/imageTypes';
import MyLoader from '@/components/MyLoader';
import useAudioPlayer from '@/context/audioPlayer/useAudioPlayer';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/Drawer';

function GlobalAudioPlayer({ isHidden = false }) {
    const {
        poster,
        title,
        // description,
        isPlaying,
        isLoading,
        seekRef,
        seekLoadedRef,
        seekSeenRef,
        seekSliderRef,
        maxRef,
        timeElapse,
        duration,
        togglePlay,
        ignoreClick,
        skipAhead,
        goBackward,
        goForward,
        updateProgress,
        closeAudioPlayer
    } = useAudioPlayer();

    const localSeekRef = useRef({});
    const localSeekLoadedRef = useRef({});
    const localSeekSeenRef = useRef({});
    const localSeekSliderRef = useRef({});

    const expandedLocalSeekRef = useRef({});
    const expandedLocalSeekLoadedRef = useRef({});
    const expandedLocalSeekSeenRef = useRef({});
    const expandedLocalSeekSliderRef = useRef({});
    const expandedLocalTimeElapseRef = useRef({});

    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        if (!isHidden) {
            seekRef.current = localSeekRef.current;
            seekLoadedRef.current = localSeekLoadedRef.current;
            seekSeenRef.current = localSeekSeenRef.current;
            seekSliderRef.current = localSeekSliderRef.current;
            /* to repaint the playbar */
            updateProgress();
        }
    }, [isHidden]);

    useEffect(() => {
        setTimeout(() => {
            if (!isExpanded) {
                seekRef.current = localSeekRef.current;
                seekLoadedRef.current = localSeekLoadedRef.current;
                seekSeenRef.current = localSeekSeenRef.current;
                seekSliderRef.current = localSeekSliderRef.current;
            } else {
                seekRef.current = expandedLocalSeekRef.current;
                seekLoadedRef.current = expandedLocalSeekLoadedRef.current;
                seekSeenRef.current = expandedLocalSeekSeenRef.current;
                seekSliderRef.current = expandedLocalSeekSliderRef.current;
                timeElapse.current = expandedLocalTimeElapseRef.current;
            }
        }, 0);
        updateProgress();
        /* to repaint the playbar */
    }, [isExpanded]);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <>
            <div
                className={`global-audio-player mx-auto max-w-[450px] z-[4] ${isHidden ? 'hidden' : ''}`}>
                <div className="audio-controls">
                    <div className="global-audio-player-progress-container">
                        <div className="audio-progress">
                            <input
                                className="seek"
                                ref={localSeekRef}
                                onInput={skipAhead}
                                onClick={ignoreClick}
                                min="0"
                                max={maxRef.current || '0'}
                                type="range"
                                step="1"
                            />
                            <div className="seek-seen loaded" ref={localSeekLoadedRef} />
                            <div className="seek-seen" ref={localSeekSeenRef} />
                            <div className="seek-slider" ref={localSeekSliderRef} />
                        </div>
                    </div>
                </div>
                <div className="global-audio-player-inner-container">
                    <button className="global-audio-player-close-btn" onClick={closeAudioPlayer}>
                        <CloseSvg />
                    </button>
                    <DorsaImage
                        src={poster}
                        className="global-audio-player-img"
                        onClick={toggleExpand}
                        config={{
                            shape: IMAGE_SHAPES.SQUARE,
                            size: IMAGE_SIZES.THUMBNAIL
                        }}
                    />
                    <MarqueeBox
                        text={title}
                        onClick={toggleExpand}
                        className="global-audio-player-title-container">
                        <h3 className="global-audio-player-title">{title}</h3>
                    </MarqueeBox>
                    <div className="global-audio-player-buttons-container">
                        {isLoading ? (
                            <MyLoader
                                className="global-audio-playback-loading"
                                strokeWidth={2}
                                width={32}
                            />
                        ) : (
                            <>
                                <button
                                    onClick={goBackward}
                                    className="global-audio-playback-jump-button global-audio-playback-jump-button-backward">
                                    <Backward10SecsSvg className="global-audio-playback-jump-button-icon" />
                                </button>
                                <button
                                    type="icon"
                                    className="global-audio-playback-play"
                                    onClick={togglePlay}>
                                    {isPlaying ? (
                                        <PauseSvg className="global-audio-playback-play-icon" />
                                    ) : (
                                        <PlaySvg className="global-audio-playback-play-icon" />
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <Drawer open={isExpanded} onOpenChange={setIsExpanded}>
                <DrawerContent className="px-6">
                    <DrawerTitle></DrawerTitle>
                    <div className="global-audio-player-container-expanded">
                        <DorsaImage
                            src={poster}
                            className="global-audio-player-expanded-img"
                            config={{
                                shape: IMAGE_SHAPES.SQUARE,
                                size: IMAGE_SIZES.SIZE_4
                            }}
                        />
                        <MarqueeBox
                            text={title}
                            className="global-audio-player-container-expanded-title-container">
                            <h3 className="global-audio-player-expanded-title">{title}</h3>
                        </MarqueeBox>
                        <div className="audio-controls">
                            <div className="audio-progress">
                                <input
                                    className="seek"
                                    ref={expandedLocalSeekRef}
                                    onInput={skipAhead}
                                    onClick={ignoreClick}
                                    min="0"
                                    max={maxRef.current || '0'}
                                    type="range"
                                    step="1"
                                />
                                <div
                                    className="seek-seen loaded"
                                    ref={expandedLocalSeekLoadedRef}
                                />
                                <div className="seek-seen played" ref={expandedLocalSeekSeenRef} />
                                <div className="seek-slider" ref={expandedLocalSeekSliderRef} />
                            </div>
                        </div>
                        <div className="global-audio-player-expanded-time-container">
                            <div>{formatTime(duration | 0)}</div>
                            <div ref={expandedLocalTimeElapseRef}>{formatTime(0)}</div>
                        </div>
                        <div className="global-audio-player-expanded-buttons-container">
                            {isLoading ? (
                                <MyLoader
                                    className="global-audio-expanded-playback-loading"
                                    strokeWidth={2}
                                    width={32}
                                />
                            ) : (
                                <>
                                    <button
                                        onClick={goForward}
                                        className="global-audio-expanded-playback-jump-button global-audio-expanded-playback-jump-button-backward">
                                        <Forward15SecsSvg className="global-audio-expanded-playback-jump-button-icon" />
                                    </button>
                                    <button
                                        type="icon"
                                        className="global-audio-expanded-playback-play"
                                        onClick={togglePlay}>
                                        {isPlaying ? (
                                            <PauseSvg className="global-audio-expanded-playback-play-icon" />
                                        ) : (
                                            <PlaySvg className="global-audio-expanded-playback-play-icon" />
                                        )}
                                    </button>
                                    <button
                                        onClick={goBackward}
                                        className="global-audio-expanded-playback-jump-button global-audio-expanded-playback-jump-button-backward">
                                        <Backward10SecsSvg className="global-audio-expanded-playback-jump-button-icon" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </DrawerContent>
            </Drawer>
        </>
    );
}

export default GlobalAudioPlayer;
